import React, { Dispatch, SetStateAction, createContext, useState } from 'react';
import { ApolloError } from '@apollo/client';
import Bugsnag from '@bugsnag/js';
import { Coord } from '@99designs/browser';

export type BaseScribble<S = Record<string, unknown>> = S & {
    id: string;
    fileId: string;
    scribblePaths: {
        scribblePathCoords: Coord[];
    }[];
};

export interface BaseScribblePathCoordsInput {
    scribblePathCoords: Coord[];
}

type EditScribble = (scribbleId: string, scribblePaths: BaseScribblePathCoordsInput[]) => void;

export type ScribbleContextValue<S, G = Record<string, unknown>> = G & {
    canScribble: boolean;
    canUndoScribble: boolean;
    scribble?: BaseScribble<S>;

    currentPath: Coord[] | null;
    setCurrentPath: Dispatch<SetStateAction<Coord[] | null>>;

    getScribbleLoading: boolean;
    getScribbleError?: ApolloError;

    createScribble: (fileId: string, newPath: BaseScribblePathCoordsInput) => void;
    editScribble: EditScribble;
    undoScribble: (scribble?: BaseScribble) => () => void;
};

export interface ScribbleProviderProps {
    fileId: string;
    children: React.ReactNode;
    initialCurrentPath?: Coord[] | null;
    canScribble?: boolean;
    canViewScribble?: boolean;
}

export interface ScribbleContext<S> {
    ScribbleProvider: ({ children }: ScribbleProviderProps) => React.ReactElement;
    useScribble: () => ScribbleContextValue<S>;
}

export function buildUndoScribble(editScribble: EditScribble) {
    return (scribble?: BaseScribble) => () => {
        if (!scribble) {
            Bugsnag.notify(new Error("Tried to undo a scribble that doesn't exist"));
            return;
        }
        const scribblePaths: BaseScribblePathCoordsInput[] = scribble.scribblePaths
            .slice(0, -1)
            .map(({ scribblePathCoords }) => ({
                scribblePathCoords: scribblePathCoords.map(({ x, y }) => ({
                    x,
                    y,
                })),
            }));
        editScribble(scribble.id, scribblePaths);
    };
}

export function createNoopScribbleContext<
    S = BaseScribble<Record<string, unknown>>
>(): ScribbleContext<S> {
    const context = createContext<ScribbleContextValue<S> | null>(null);

    function ScribbleProvider({
        children,
        initialCurrentPath = null,
        canScribble = false,
    }: ScribbleProviderProps) {
        const [currentPath, setCurrentPath] = useState<Coord[] | null>(initialCurrentPath);
        const noop = () => undefined;
        const createScribble: ScribbleContextValue<S>['createScribble'] = noop;
        const editScribble: ScribbleContextValue<S>['editScribble'] = noop;
        const undoScribble: ScribbleContextValue<S>['undoScribble'] =
            buildUndoScribble(editScribble);

        return (
            <context.Provider
                value={{
                    currentPath,
                    setCurrentPath,
                    getScribbleLoading: false,
                    getScribbleError: undefined,
                    createScribble,
                    editScribble,
                    undoScribble,
                    scribble: undefined,
                    canScribble,
                    canUndoScribble: false,
                }}
            >
                {children}
            </context.Provider>
        );
    }

    function useNoopScribbleContext(): ScribbleContextValue<S> {
        const value = React.useContext(context);
        if (!value) {
            throw new Error('useNoopScribbleContext must be used within a ScribbleProvider');
        }
        return value;
    }

    return {
        ScribbleProvider: ScribbleProvider,
        useScribble: useNoopScribbleContext,
    };
}
